import { useState, useMemo, useEffect } from 'react';
import { useSearchParams, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../utils/Auth';
import TypeSelect from '../../components/QrComponents/TypeSelect';
import QrRules from '../../components/QrComponents/QrRules';
import Tabs from './Tabs';
import Options from '../../components/QrComponents/Options';
import SaveBtn from './SaveBtn';
import LinkQR from '../../components/QrComponents/LinkQR';
import { useQR } from '../../utils/hooks/useQR';
import { AnimatePresence, motion } from 'motion/react';
import { transition, variants } from './variants';
import { useLocalStorage } from '../../utils/hooks/useLocalStorage';
import { useBack } from '../../utils/hooks/useBack';
import styles from './QrEdit.module.css';

export default function QrEdit(props) {
    // ----- STATE INITIALIZATION -----
    const { reference } = props;
    const { qr_data, activeOrgId, fileHandler: { filesReset } } = useAuth();
    const navigate = useNavigate();

    const [, setStoredQrId] = useLocalStorage('qr_id');
    const [ searchParams, setSearchParams ] = useSearchParams();

    const params = useParams();
    useQR(reference, params?.id || '');

    const ref = useMemo(() => searchParams.get('ref') || '', [searchParams.get('ref')]);
    const tab = useMemo(() => searchParams.get('tab') || '', [searchParams.get('tab')]);
    const qrType = useMemo(() => qr_data?.data?.qr_type || '', [qr_data?.data?.qr_type]);

    const [linking, setLinking] = useState(false);
    const [ parent, setParent ] = useState({ obj: null, selected: false });

    const goBack = useBack();

    useEffect(() => {
        if (!qr_data?.data?.org_id || !activeOrgId) return;
        if (qr_data.data.org_id !== activeOrgId) {
            navigate('/organizations', { replace: true });
        }
    }, [qr_data?.data?.org_id, activeOrgId, navigate]);
    
    // ----- END STATE INITIALIZATION -----
    // ----------------------------------------------

    const handleMainReturn = useMemo(() => {
        switch (tab) {
            case 'configure':
                return (
                    <motion.div
                        key='configure'
                        className={styles.motionWrap}
                        transition={transition}
                        variants={variants}
                        initial='initial'
                        animate='animate'
                        exit='initial'
                    >
                        <Options qr_type={qrType} reference={reference} />
                    </motion.div>
                );
            case 'schedule':
                return (
                    <motion.div
                        key='schedule'
                        className={styles.motionWrap}
                        transition={transition}
                        variants={variants}
                        initial='initial'
                        animate='animate'
                        exit='initial'
                    >
                        <QrRules />
                    </motion.div>
                );
            default:
                return (
                    <AnimatePresence mode='wait'>
                        {linking ? (
                            <motion.div
                                key="link-qr"
                                className={styles.motionWrap}
                                transition={transition}
                                variants={variants}
                                initial="initial"
                                animate="animate"
                                exit="initial"
                            >
                                <LinkQR parent={parent} setParent={setParent} />
                            </motion.div>
                        ) : (
                            <motion.div
                                key="type-select"
                                className={styles.motionWrap}
                                transition={transition}
                                variants={variants}
                                initial="initial"
                                animate="animate"
                                exit="initial"
                            >
                                <TypeSelect />
                            </motion.div>
                        )}
                    </AnimatePresence>
                );
        }
    }, [tab, qrType, reference, linking, parent]);

    const handleBack = () => {
        // update
        filesReset();
        setStoredQrId(null);

        if (linking) {
            setLinking(false);
            setParent({ obj: null, selected: false });
        } else {
            goBack();
        };
    };

    return (
        <div className={styles.wrapper}>
            <div className={styles.container}>
                <Tabs tab={tab} />
                <AnimatePresence mode='wait'>
                    <motion.div
                        key='editor' 
                        className={styles.editor}
                        transition={{...transition, duration: .5}}
                        variants={variants}
                        initial='initial'
                        animate='animate'
                        exit='initial'
                    >
                        <div className={styles.header}>
                            <div className={styles.btnContainer}>
                                <button className={styles.backBtn} onClick={handleBack} style={window.location.pathname.includes('setup') ? {display: 'none'} : null}><i className='fas fa-arrow-left' style={{pointerEvents: 'none'}} />Back</button>
                                {tab && (<button className={styles.changeBtn} style={window.location.pathname.includes('setup') ? {left: '1rem'} : null} disabled={!tab} onClick={() => setSearchParams({})} title='Change QR Type'>{window.innerWidth > 768 ? 'Change QR Type' : <><i className='far fa-swap' /> Type</>}</button>)}
                            </div>
                             {!tab && (
                                 <div className={styles.typeHeaderContainer}>
                                     <h4 className={styles.typeHeader}>{linking ? 'Liquid Link' : 'Select a LiquidQR Type'}</h4>
                                     <div className={styles.separator}>- or -</div>
                                     <button 
                                         className={styles.linkToggleBtn} 
                                         onClick={() => setLinking(!linking)}
                                     >
                                         {linking ? 'New QR' : 'Link to an existing QR'}
                                     </button>
                                 </div>
                             )}
                            <div className={styles.saveBtnContainer}>
                                <SaveBtn reference={reference} ref={ref} linking={linking} parent={parent} />
                            </div>
                        </div>
                        <div className={styles.editorInner}>
                            <AnimatePresence mode='wait'>
                                {handleMainReturn}
                            </AnimatePresence>
                        </div>
                    </motion.div>
                </AnimatePresence>
            </div>
        </div>
    );
};
